import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DealsPipeLineService } from 'src/app/services/deals-pipe-line.service';
import { AfterSalesSubStatusService } from 'src/app/services/after-sales-sub-status.service';
import { DealsEntityService } from 'src/app/services/deals-entity.service';
import { DealsStageService } from 'src/app/services/deals-stage.service';
import { DownloadImageService } from 'src/app/services/download-image.service';
import { MatDialog } from '@angular/material/dialog';
import { SubStatusUpdateDialogComponent } from './sub-status-update-dialog.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-after-sales-pipeline-tracker',
  templateUrl: './after-sales-pipeline-tracker.component.html',
  styleUrls: ['./after-sales-pipeline-tracker.component.scss']
})
export class AfterSalesPipelineTrackerComponent implements OnInit, OnChanges {

  /** Pass the full deal/lead object */
  @Input() entityObject: any;

  /** 'deal' | 'lead' — determines which API to call on sub-status update */
  @Input() entityType: 'deal' | 'lead' = 'deal';

  @Input() dealStages: any[] = [];

  /** Emits after a successful sub-status update so parent can reload */
  @Output() statusUpdated = new EventEmitter<void>();

  @Output() advanceDealStage = new EventEmitter<any>();

  stageList: any[] = [];
  subStatusList: any[] = [];
  private _historyList: any[] = [];
  get historyList(): any[] {
    return this._historyList;
  }
  set historyList(val: any[]) {
    this._historyList = val || [];
    this.refreshHistoryDerivedData();
  }
  showHistory: boolean = false;
  isUpdating: boolean = false;
  isLoading: boolean = false;

  currentPipelineId: number | null = null;
  currentPipelineName: string = '';
  currentStageIndex: number = -1;
  currentSubStatusIndex: number = -1;

  get hasPipeline(): boolean {
    return !!(this.currentPipelineId && this.stageList && this.stageList.length > 0);
  }

  get currentStage(): any {
    return this.stageList[this.currentStageIndex] || null;
  }

  get isLastStage(): boolean {
    return this.stageList && this.stageList.length > 0 && this.currentStageIndex === this.stageList.length - 1;
  }

  get isLastStageCompleted(): boolean {
    if (!this.isLastStage) {
      return false;
    }
    if (this.entityObject?.afterSalesCompletedDate) {
      return true;
    }
    if (this.entityObject?.afterSalesStatus && this.entityObject.afterSalesStatus.trim().toLowerCase() === 'completed') {
      return true;
    }
    if (this.subStatusList && this.subStatusList.length > 0) {
      const lastSub = this.subStatusList[this.subStatusList.length - 1];
      const hasHistoryForLastSub = (this.historyList || []).some((h: any) =>
        (Number(h.toSubStatusId) === Number(lastSub.statusId) || Number(h.toSubStatusId) === Number(lastSub.id)) ||
        (h.toSubStatusName && lastSub.statusName && h.toSubStatusName.trim().toLowerCase() === lastSub.statusName.trim().toLowerCase())
      );
      if (hasHistoryForLastSub && this.currentSubStatusIndex === this.subStatusList.length - 1) {
        return true;
      }
    } else if (this.subStatusList && this.subStatusList.length === 0) {
      const currentStage = this.stageList[this.currentStageIndex];
      const hasHistoryForStage = (this.historyList || []).some((h: any) =>
        (Number(h.stageId) === Number(currentStage?.pipelineStageId) || Number(h.stageId) === Number(currentStage?.statusId)) ||
        (h.stageName && currentStage?.statusName && h.stageName.trim().toLowerCase() === currentStage.statusName.trim().toLowerCase())
      );
      if (hasHistoryForStage) {
        return true;
      }
    }
    return false;
  }

  constructor(
    private dealsPipeLineService: DealsPipeLineService,
    private afterSalesSubStatusService: AfterSalesSubStatusService,
    private dealsEntityService: DealsEntityService,
    private dealsStageService: DealsStageService,
    private downloadImageService: DownloadImageService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityObject'] || changes['dealStages']) {
      this.loadData();
    }
  }

  private lastLoadedEntityId: any = null;
  private lastLoadedStatusId: any = null;
  private lastLoadedStatusName: any = null;
  private lastLoadedAfterSalesStageId: any = null;
  private lastLoadedAfterSalesSubStatusId: any = null;
  private lastLoadedAfterSalesStatus: any = null;

  loadData(forceReload: boolean = false): void {
    if (!this.entityObject?.entityId) {
      this.clearPipeline();
      this.lastLoadedEntityId = null;
      return;
    }

    const currentStatusId = this.entityObject.statusId ?? this.entityObject.leadStatus ?? this.entityObject.Stage;
    const currentStatusName = this.entityObject.statusName || this.entityObject.dealStage || this.entityObject.stage;
    const currentAfterSalesStageId = this.entityObject.afterSalesStageId;
    const currentAfterSalesSubStatusId = this.entityObject.afterSalesSubStatusId;
    const currentAfterSalesStatus = this.entityObject.afterSalesStatus;

    if (!forceReload &&
        this.lastLoadedEntityId === this.entityObject.entityId &&
        this.lastLoadedStatusId === currentStatusId &&
        this.lastLoadedStatusName === currentStatusName &&
        this.lastLoadedAfterSalesStageId === currentAfterSalesStageId &&
        this.lastLoadedAfterSalesSubStatusId === currentAfterSalesSubStatusId &&
        this.lastLoadedAfterSalesStatus === currentAfterSalesStatus &&
        this.dealStages && this.dealStages.length > 0 &&
        this.currentPipelineId !== null) {
      return;
    }

    this.lastLoadedEntityId = this.entityObject.entityId;
    this.lastLoadedStatusId = currentStatusId;
    this.lastLoadedStatusName = currentStatusName;
    this.lastLoadedAfterSalesStageId = currentAfterSalesStageId;
    this.lastLoadedAfterSalesSubStatusId = currentAfterSalesSubStatusId;
    this.lastLoadedAfterSalesStatus = currentAfterSalesStatus;

    if (this.dealStages && this.dealStages.length > 0) {
      this.processStagePipeline(this.dealStages, currentStatusId, currentStatusName);
    } else {
      const typeId = this.entityType === 'lead' ? 2 : 4;
      this.dealsStageService.getAllDealsStagesDropDown(typeId).subscribe({
        next: (stages: any) => {
          const stageList = Array.isArray(stages) ? stages : (stages?.data || stages?.content || []);
          this.dealStages = stageList;
          this.processStagePipeline(stageList, currentStatusId, currentStatusName);
        },
        error: () => {
          this.clearPipeline();
        }
      });
    }
  }

  private processStagePipeline(stages: any[], statusId: any, statusName: string): void {
    const matchedStage = (stages || []).find((s: any) => {
      const idMatches = (statusId !== undefined && statusId !== null && statusId !== '')
        ? (String(s.statusId) === String(statusId) || String(s.id) === String(statusId))
        : false;

      const nameMatches = (statusName && s.statusName)
        ? (s.statusName.trim().toLowerCase() === statusName.trim().toLowerCase())
        : false;

      if (idMatches && nameMatches) return true;
      if (idMatches && !statusName) return true;
      if (nameMatches && (statusId === undefined || statusId === null || statusId === '')) return true;

      return false;
    });

    const finalMatchedStage = matchedStage || (stages || []).find((s: any) => {
      if (statusName && s.statusName && s.statusName.trim().toLowerCase() === statusName.trim().toLowerCase()) return true;
      if (statusId !== undefined && statusId !== null && statusId !== '' && (String(s.statusId) === String(statusId) || String(s.id) === String(statusId))) return true;
      return false;
    });

    if (!finalMatchedStage) {
      this.clearPipeline();
      return;
    }

    let rawPipelineIds: any = finalMatchedStage?.pipelineIds ?? finalMatchedStage?.pipelineId ?? finalMatchedStage?.associatedPipelineId ?? finalMatchedStage?.pipeLineId ?? finalMatchedStage?.pipeLineIds;
    let pipelineIds: number[] = [];
    if (Array.isArray(rawPipelineIds)) {
      pipelineIds = rawPipelineIds.map(id => Number(id)).filter(id => !isNaN(id) && id > 0);
    } else if (rawPipelineIds !== undefined && rawPipelineIds !== null && Number(rawPipelineIds) > 0) {
      pipelineIds = [Number(rawPipelineIds)];
    }

    // Stage has no after-sales pipeline linked
    if (!pipelineIds || pipelineIds.length === 0) {
      this.clearPipeline();
      return;
    }

    const targetPipelineId = Number(pipelineIds[0]);
    this.loadPipelineDetails(targetPipelineId);
  }

  private loadPipelineDetails(pipelineId: number): void {
    this.isLoading = true;

    forkJoin({
      pipe: this.dealsPipeLineService.getDealsPipeLineByIdAndActive(pipelineId).pipe(catchError(() => of(null))),
      historyRes: this.dealsEntityService.getAfterSalesHistory(this.entityObject.entityId).pipe(catchError(() => of(null))),
      stagesRes: this.dealsPipeLineService.getDealsPipeLineStagesById(pipelineId).pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ pipe, historyRes, stagesRes }) => {
        if (!pipe || (pipe.active !== undefined && Number(pipe.active) === 0)) {
          this.clearPipeline();
          return;
        }

        this.currentPipelineId = pipelineId;
        this.currentPipelineName = pipe.pipelineName || pipe.name || '';

        const allHistory = historyRes?.datalist || historyRes || [];
        this.historyList = allHistory;

        const pipelineHistory = allHistory.filter((h: any) => Number(h.pipelineId) === Number(pipelineId));
        const latestHistory = pipelineHistory.length > 0 ? pipelineHistory[0] : null;

        const rawStages = Array.isArray(stagesRes) ? stagesRes : [];
        this.stageList = rawStages.sort((a: any, b: any) => a.position - b.position);

        if (this.stageList.length === 0) {
          this.clearPipeline();
          return;
        }

        let matchedStage: any = null;
        let activeSubStatusId: number | null = null;

        // 1. Try match by entityObject.afterSalesStageId (pipelineStageId, statusId, id)
        if (this.entityObject?.afterSalesStageId) {
          const targetId = Number(this.entityObject.afterSalesStageId);
          matchedStage = this.stageList.find((s: any) =>
            Number(s.pipelineStageId) === targetId ||
            Number(s.statusId) === targetId ||
            Number(s.id) === targetId
          );
        }

        // 2. Try match by entityObject.afterSalesStatus (stage name)
        if (!matchedStage && this.entityObject?.afterSalesStatus) {
          const targetName = String(this.entityObject.afterSalesStatus).trim().toLowerCase();
          matchedStage = this.stageList.find((s: any) =>
            s.statusName && String(s.statusName).trim().toLowerCase() === targetName
          );
        }

        // 3. Try match by latestHistory (stageId or stageName)
        if (!matchedStage && latestHistory) {
          if (latestHistory.stageId) {
            const histStageId = Number(latestHistory.stageId);
            matchedStage = this.stageList.find((s: any) =>
              Number(s.pipelineStageId) === histStageId ||
              Number(s.statusId) === histStageId ||
              Number(s.id) === histStageId
            );
          }
          if (!matchedStage && latestHistory.stageName) {
            const histStageName = String(latestHistory.stageName).trim().toLowerCase();
            matchedStage = this.stageList.find((s: any) =>
              s.statusName && String(s.statusName).trim().toLowerCase() === histStageName
            );
          }
        }

        // 4. Fallback to first stage
        if (!matchedStage) {
          matchedStage = this.stageList[0];
        }

        const stageIndex = this.stageList.indexOf(matchedStage);
        this.currentStageIndex = stageIndex >= 0 ? stageIndex : 0;
        const activeStageId = matchedStage.pipelineStageId || matchedStage.id || matchedStage.statusId;

        if (this.entityObject?.afterSalesSubStatusId) {
          activeSubStatusId = this.entityObject.afterSalesSubStatusId;
        } else if (latestHistory && latestHistory.toSubStatusId) {
          activeSubStatusId = latestHistory.toSubStatusId;
        }

        this.entityObject.afterSalesPipelineId = pipelineId;
        this.entityObject.afterSalesStageId = activeStageId;
        this.entityObject.afterSalesStatus = this.stageList[this.currentStageIndex].statusName;

        if (activeStageId) {
          this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(activeStageId).subscribe({
            next: (subs: any) => {
              const rawSubs = Array.isArray(subs) ? subs : (subs?.data || subs?.content || []);
              this.subStatusList = rawSubs.slice().sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));

              if (activeSubStatusId) {
                this.currentSubStatusIndex = this.subStatusList.findIndex(
                  (s: any) => Number(s.statusId) === Number(activeSubStatusId) ||
                              Number(s.id) === Number(activeSubStatusId) ||
                              (s.statusName && latestHistory?.toSubStatusName && s.statusName.trim().toLowerCase() === latestHistory.toSubStatusName.trim().toLowerCase())
                );
              } else {
                this.currentSubStatusIndex = this.subStatusList.length > 0 ? 0 : -1;
              }

              if (this.currentSubStatusIndex === -1 && this.subStatusList.length > 0) {
                this.currentSubStatusIndex = 0;
              }

              if (this.currentSubStatusIndex >= 0 && this.subStatusList[this.currentSubStatusIndex]) {
                this.entityObject.afterSalesSubStatusId = this.subStatusList[this.currentSubStatusIndex].statusId;
              }

              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: () => {
              this.subStatusList = [];
              this.currentSubStatusIndex = -1;
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          });
        } else {
          this.subStatusList = [];
          this.currentSubStatusIndex = -1;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.clearPipeline();
        this.cdr.detectChanges();
      }
    });
  }

  private clearPipeline(): void {
    this.currentPipelineId = null;
    this.currentPipelineName = '';
    this.stageList = [];
    this.subStatusList = [];
    this.historyList = [];
    this.currentStageIndex = -1;
    this.currentSubStatusIndex = -1;
    this.lastLoadedEntityId = null;
    this.lastLoadedStatusId = null;
    this.lastLoadedStatusName = null;
    this.isLoading = false;
  }

  openCurrentSubStatusDialog(): void {
    if (this.isLastStageCompleted) return;
    if (this.currentSubStatusIndex >= 0 && this.subStatusList[this.currentSubStatusIndex]) {
      this.openSubStatusDialog(this.subStatusList[this.currentSubStatusIndex], this.currentSubStatusIndex);
    } else if (this.subStatusList.length > 0) {
      this.openSubStatusDialog(this.subStatusList[0], 0);
    }
  }

  selectSubStatus(sub: any, index: number): void {
    if (this.isLastStageCompleted) return;
    this.openSubStatusDialog(sub, index);
  }

  openSubStatusDialog(sub: any, index: number): void {
    if (this.isUpdating) return;
    if (!this.entityObject?.entityId) return;

    const isFinalStage = index === this.subStatusList.length - 1;
    const requireNotes = sub.requireNotes === 1;
    const requireImages = sub.requireImages === 1;
    const hasDynamicForm = sub.dynamicFormGroupId && Number(sub.dynamicFormGroupId) > 0;

    const dialogRef = this.dialog.open(SubStatusUpdateDialogComponent, {
      width: hasDynamicForm ? '700px' : '550px',
      data: {
        dialogTitle: `Update Sub-Status: ${sub.statusName}`,
        nodeName: sub.statusName,
        buttonText: 'Update & Save',
        isFinalStage: isFinalStage,
        requireNotes: requireNotes,
        requireImages: requireImages,
        dynamicFormGroupId: sub.dynamicFormGroupId,
        entityObject: this.entityObject,
        entityType: this.entityType,
        dealStages: this.dealStages,
        currentDealStage: this.entityObject?.statusName || this.entityObject?.dealStage || this.entityObject?.stage,
        saveCallback: (payload: any) => this.performUpdate(sub, index, payload)
      }
    });

    dialogRef.afterClosed().subscribe(() => {
    });
  }

  private performUpdate(sub: any, index: number, payload: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isUpdating = true;
      const enrichedPayload = {
        ...payload,
        pipelineId: String(this.currentPipelineId || this.entityObject?.afterSalesPipelineId || ''),
        stageId: String(this.entityObject?.afterSalesStageId || '')
      };
      this.dealsEntityService.updateAfterSalesSubStatusWithImages(this.entityObject.entityId, sub.statusId, enrichedPayload).subscribe({
        next: () => {
          this.snackBar.open('After-Sales status updated!', '×', { panelClass: 'success', duration: 3000 });
          this.entityObject.afterSalesSubStatusId = sub.statusId;
          this.currentSubStatusIndex = index;

          const isLastStage = this.currentStageIndex === this.stageList.length - 1;
          const isLastSub = index === this.subStatusList.length - 1 || this.subStatusList.length === 0;
          if (isLastStage && isLastSub) {
            this.entityObject.afterSalesCompletedDate = new Date().toISOString();
          }

          // Statically persist dynamic form field values to deal entity
          if (payload?.dynamicFormValues && Object.keys(payload.dynamicFormValues).length > 0) {
            this.entityObject.customField10 = {
              ...(this.entityObject.customField10 || {}),
              ...payload.dynamicFormValues
            };
            Object.assign(this.entityObject, payload.dynamicFormValues);

            const dealPayload = {
              ...this.entityObject,
              ...payload.dynamicFormValues,
              customField10: this.entityObject.customField10
            };
            this.dealsEntityService.editDeals(dealPayload, this.entityObject.entityId).subscribe({
              next: () => { },
              error: (err) => console.warn('Deal entity static fields save returned:', err)
            });
          }

          this.isUpdating = false;
          this.lastLoadedEntityId = null;
          if (this.showHistory) {
            this.loadHistory();
          }
          if (this.currentPipelineId) {
            this.loadPipelineDetails(this.currentPipelineId);
          }
          this.statusUpdated.emit();
          this.cdr.detectChanges();
          resolve();
        },
        error: (err) => {
          this.snackBar.open(err?.error?.status_message || err?.error || 'Failed to update status', '×', { panelClass: 'error', duration: 4000 });
          this.isUpdating = false;
          this.cdr.detectChanges();
          reject(err);
        }
      });
    });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory && this.historyList.length === 0) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    const dealId = this.entityObject?.entityId || this.entityObject?.id;
    if (!dealId) return;
    this.dealsEntityService.getAfterSalesHistory(dealId).subscribe((res: any) => {
      this.historyList = res?.datalist || res || [];
      this.cdr.detectChanges();
    });
  }

  getSubStatusClass(index: number): string {
    if (index < this.currentSubStatusIndex || (index === this.currentSubStatusIndex && this.isLastStageCompleted)) {
      return 'step-done';
    }
    if (index === this.currentSubStatusIndex) return 'step-active';
    return 'step-pending';
  }

  getStageClass(index: number): string {
    if (index < this.currentStageIndex || (index === this.currentStageIndex && this.isLastStageCompleted)) {
      return 'stage-done';
    }
    if (index === this.currentStageIndex) return 'stage-active';
    return 'stage-pending';
  }

  /** Called when current stage has no sub-statuses or ready to advance */
  advanceToNextStageManually(): void {
    if (this.isUpdating) return;
    const dealId = this.entityObject?.entityId || this.entityObject?.id;
    if (!dealId) return;

    const nextStageIndex = this.currentStageIndex + 1;
    if (nextStageIndex >= this.stageList.length) return;

    const nextStage = this.stageList[nextStageIndex];
    const targetStageId = nextStage?.pipelineStageId || nextStage?.id || nextStage?.statusId;
    if (!targetStageId) return;

    // Fetch first sub-status of next stage to check if it has dynamic form or required flags
    this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(targetStageId).subscribe({
      next: (nextSubs: any) => {
        const rawSubs = Array.isArray(nextSubs) ? nextSubs : (nextSubs?.data || nextSubs?.content || []);
        const sortedNextSubs = rawSubs.slice().sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
        const firstNextSub = sortedNextSubs.length > 0 ? sortedNextSubs[0] : null;

        const isFinalStage = nextStageIndex === this.stageList.length - 1;
        const requireNotes = firstNextSub ? (firstNextSub.requireNotes === 1) : false;
        const requireImages = firstNextSub ? (firstNextSub.requireImages === 1) : false;
        const dynamicFormGroupId = (firstNextSub && Number(firstNextSub.dynamicFormGroupId) > 0)
          ? firstNextSub.dynamicFormGroupId
          : null;
        const hasDynamicForm = !!(dynamicFormGroupId && Number(dynamicFormGroupId) > 0);

        const dialogRef = this.dialog.open(SubStatusUpdateDialogComponent, {
          width: hasDynamicForm ? '700px' : '550px',
          data: {
            dialogTitle: `Move to Next Stage: ${nextStage.statusName}`,
            nodeName: firstNextSub ? `${nextStage.statusName} — ${firstNextSub.statusName}` : nextStage.statusName,
            buttonText: `Move to ${nextStage.statusName}`,
            isFinalStage: isFinalStage,
            requireNotes: requireNotes,
            requireImages: requireImages,
            dynamicFormGroupId: dynamicFormGroupId,
            entityObject: this.entityObject,
            entityType: this.entityType,
            dealStages: this.dealStages,
            currentDealStage: this.entityObject?.statusName || this.entityObject?.dealStage || this.entityObject?.stage,
            saveCallback: (payload: any) => this.performStageAdvance(nextStage, nextStageIndex, payload)
          }
        });
      },
      error: () => {
        const isFinalStage = nextStageIndex === this.stageList.length - 1;
        const dialogRef = this.dialog.open(SubStatusUpdateDialogComponent, {
          width: '550px',
          data: {
            dialogTitle: `Move to Next Stage: ${nextStage.statusName}`,
            nodeName: nextStage.statusName,
            buttonText: `Move to ${nextStage.statusName}`,
            isFinalStage: isFinalStage,
            requireNotes: false,
            requireImages: false,
            dynamicFormGroupId: null,
            entityObject: this.entityObject,
            entityType: this.entityType,
            dealStages: this.dealStages,
            currentDealStage: this.entityObject?.statusName || this.entityObject?.dealStage || this.entityObject?.stage,
            saveCallback: (payload: any) => this.performStageAdvance(nextStage, nextStageIndex, payload)
          }
        });
      }
    });
  }

  private performStageAdvance(nextStage: any, nextStageIndex: number, payload: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isUpdating = true;
      const targetStageId = nextStage?.pipelineStageId || nextStage?.id || nextStage?.statusId;
      const enrichedPayload = {
        ...payload,
        pipelineId: String(this.currentPipelineId || this.entityObject?.afterSalesPipelineId || ''),
        stageId: String(targetStageId || '')
      };

      const dealId = this.entityObject?.entityId || this.entityObject?.id;

      this.dealsEntityService.advanceAfterSalesStage(dealId, targetStageId, enrichedPayload).subscribe({
        next: () => {
          this.isUpdating = false;
          this.snackBar.open(`Moved to ${nextStage.statusName}!`, '×', { panelClass: 'success', duration: 3000 });
          this.entityObject.afterSalesStageId = targetStageId;
          this.entityObject.afterSalesStatus = nextStage.statusName;
          this.currentStageIndex = nextStageIndex;
          this.lastLoadedEntityId = null;

          if (payload?.dynamicFormValues && Object.keys(payload.dynamicFormValues).length > 0) {
            this.entityObject.customField10 = {
              ...(this.entityObject.customField10 || {}),
              ...payload.dynamicFormValues
            };
            Object.assign(this.entityObject, payload.dynamicFormValues);

            const dealPayload = {
              ...this.entityObject,
              ...payload.dynamicFormValues,
              customField10: this.entityObject.customField10
            };
            this.dealsEntityService.editDeals(dealPayload, dealId).subscribe({
              next: () => { },
              error: (err) => console.warn('Deal entity static fields save returned:', err)
            });
          }

          if (this.showHistory) {
            this.loadHistory();
          }
          if (this.currentPipelineId || enrichedPayload.pipelineId) {
            this.loadPipelineDetails(Number(this.currentPipelineId || enrichedPayload.pipelineId));
          }
          this.statusUpdated.emit();
          this.cdr.detectChanges();
          resolve();
        },
        error: (err) => {
          this.isUpdating = false;
          this.snackBar.open(err?.error?.status_message || err?.error || 'Failed to advance stage', '×', { panelClass: 'error', duration: 4000 });
          this.cdr.detectChanges();
          reject(err);
        }
      });
    });
  }

  public serverDocPath = environment.commonImageApi + 'stageHistory/';
  public historyViewMode: 'timeline' | 'documents' | 'form-data' = 'timeline';

  getFileUrl(fileName: string): string {
    if (!fileName) return '';
    if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
      return fileName;
    }
    return this.serverDocPath + fileName;
  }

  parseHistoryDocuments(imagesStr: any): string[] {
    if (!imagesStr) return [];
    if (Array.isArray(imagesStr)) return imagesStr;
    try {
      const parsed = JSON.parse(imagesStr);
      if (Array.isArray(parsed)) return parsed;
      return [imagesStr];
    } catch (e) {
      return String(imagesStr).includes(',') ? String(imagesStr).split(',').map(s => s.trim()) : [String(imagesStr)];
    }
  }

  parseFormValues(formValuesStr: any): Array<{ key: string; label: string; value: any }> {
    if (!formValuesStr) return [];
    try {
      const obj = typeof formValuesStr === 'string' ? JSON.parse(formValuesStr) : formValuesStr;
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
      return Object.keys(obj).map(key => ({
        key,
        label: this.formatFieldLabel(key),
        value: this.formatFieldValue(obj[key])
      })).filter(item => item.value !== null && item.value !== undefined && item.value !== '');
    } catch (e) {
      return [];
    }
  }

  formatFieldLabel(key: string): string {
    if (!key) return '';
    const result = key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ');
    return result.charAt(0).toUpperCase() + result.slice(1).trim();
  }

  formatFieldValue(val: any): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      if (val instanceof Date) {
        return new DatePipe('en-US').transform(val, 'dd MMM yyyy') || String(val);
      }
      try {
        return JSON.stringify(val);
      } catch (e) {
        return String(val);
      }
    }
    const strVal = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:?\d{2}|Z)?)?$/.test(strVal)) {
      const parsedDate = new Date(strVal);
      if (!isNaN(parsedDate.getTime())) {
        return new DatePipe('en-US').transform(parsedDate, 'dd MMM yyyy') || strVal;
      }
    }
    return strVal;
  }

  private _allHistoryDocuments: Array<{
    fileName: string;
    stageName: string;
    subStatusName: string;
    uploadedBy: string;
    uploadedAt: any;
    fileUrl: string;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    isExcel: boolean;
    isVideo: boolean;
    isAudio: boolean;
    fileTypeLabel: string;
  }> = [];

  private _allHistoryFormEntries: Array<{
    stageName: string;
    subStatusName: string;
    submittedBy: string;
    submittedAt: any;
    fields: Array<{ key: string; label: string; value: any }>;
    // }> {
    //   const entries: any[] = [];
    //   if (!this.historyList || this.historyList.length === 0) return entries;

    //   this.historyList.forEach(h => {
    //     if (h.formValues) {
    //       const fields = this.parseFormValues(h.formValues);
    //       if (fields.length > 0) {
    //         entries.push({
    //           stageName: h.stageName || '',
    //           subStatusName: h.toSubStatusName || '',
    //           submittedBy: h.changedByName || '',
    //           submittedAt: h.changedAt,
    //           fields: fields
    //         });
    //       }
    //     }
    //   });
    //   return entries;
    // }
  }> = [];
  get allHistoryDocuments(): Array<{
    fileName: string;
    stageName: string;
    subStatusName: string;
    uploadedBy: string;
    uploadedAt: any;
    fileUrl: string;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    isExcel: boolean;
    isVideo: boolean;
    isAudio: boolean;
    fileTypeLabel: string;
  }> {
    return this._allHistoryDocuments;
  }

  get allHistoryFormEntries(): Array<{
    stageName: string;
    subStatusName: string;
    submittedBy: string;
    submittedAt: any;
    fields: Array<{ key: string; label: string; value: any }>;
  }> {
    return this._allHistoryFormEntries;
  }

  private refreshHistoryDerivedData(): void {
    // 1. Documents
    const docs: any[] = [];
    if (this._historyList && this._historyList.length > 0) {
      this._historyList.forEach(h => {
        if (h.documents && Array.isArray(h.documents) && h.documents.length > 0) {
          h.documents.forEach((d: any) => {
            const fname = d.fileName || d.documentName || d.name || '';
            if (fname) {
              const cleanFname = fname.split('/').pop() || fname;
              docs.push({
                fileName: cleanFname,
                stageName: d.stageName || h.stageName || '',
                subStatusName: d.subStatusName || h.toSubStatusName || '',
                uploadedBy: d.uploadedBy || h.changedByName || '',
                uploadedAt: d.createdAt || h.changedAt,
                fileUrl: this.getFileUrl(fname),
                isImage: this.isImageDoc(cleanFname),
                isPdf: this.isPdfDoc(cleanFname),
                isWord: this.isWordDoc(cleanFname),
                isExcel: this.isExcelDoc(cleanFname),
                isVideo: this.isVideoDoc(cleanFname),
                isAudio: this.isAudioDoc(cleanFname),
                fileTypeLabel: this.getFileTypeLabel(cleanFname)
              });
            }
          });
        } else if (h.images) {
          const parsedFiles = this.parseHistoryDocuments(h.images);
          parsedFiles.forEach((fname: string) => {
            if (fname && fname.trim()) {
              const cleanFname = fname.trim().split('/').pop() || fname.trim();
              docs.push({
                fileName: cleanFname,
                stageName: h.stageName || '',
                subStatusName: h.toSubStatusName || '',
                uploadedBy: h.changedByName || '',
                uploadedAt: h.changedAt,
                fileUrl: this.getFileUrl(fname.trim()),
                isImage: this.isImageDoc(cleanFname),
                isPdf: this.isPdfDoc(cleanFname),
                isWord: this.isWordDoc(cleanFname),
                isExcel: this.isExcelDoc(cleanFname),
                isVideo: this.isVideoDoc(cleanFname),
                isAudio: this.isAudioDoc(cleanFname),
                fileTypeLabel: this.getFileTypeLabel(cleanFname)
              });
            }
          });
        }
      });
    }
    this._allHistoryDocuments = docs;

    // 2. Form entries
    const entries: any[] = [];
    if (this._historyList && this._historyList.length > 0) {
      this._historyList.forEach(h => {
        if (h.formValues) {
          const fields = this.parseFormValues(h.formValues);
          if (fields.length > 0) {
            entries.push({
              stageName: h.stageName || '',
              subStatusName: h.toSubStatusName || '',
              submittedBy: h.changedByName || '',
              submittedAt: h.changedAt,
              fields: fields
            });
          }
        }
      });
    }
    this._allHistoryFormEntries = entries;
  }

  trackByDoc(index: number, item: any): string {
    return item ? (item.fileUrl || item.fileName || index.toString()) : index.toString();
  }

  trackByHistory(index: number, item: any): string | number {
    return item ? (item.historyId || item.id || index) : index;
  }

  trackByDocName(index: number, item: string): string {
    return item || index.toString();
  }

  trackByEntry(index: number, item: any): string | number {
    return item ? (item.submittedAt || index) : index;
  }

  trackByField(index: number, item: any): string {
    return item ? (item.key || index.toString()) : index.toString();
  }

  downloadDocument(fileUrl: string, fileName?: string): void {
    if (!fileUrl) return;
    const name = fileName || fileUrl.split('fileName=').pop()?.split('/').pop() || 'download';
    this.downloadImageService.downloadImageNew(fileUrl, name);
  }

  isImageDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
  }

  isPdfDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ext === 'pdf';
  }

  isWordDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['doc', 'docx'].includes(ext);
  }

  isExcelDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['xls', 'xlsx', 'csv'].includes(ext);
  }

  isVideoDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext);
  }

  isAudioDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
  }

  getFileTypeLabel(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (this.isPdfDoc(fileName)) return 'application/pdf';
    if (this.isWordDoc(fileName)) return 'application/vnd.word';
    if (this.isExcelDoc(fileName)) return 'application/vnd.excel';
    if (this.isImageDoc(fileName)) return `image/${ext}`;
    if (this.isVideoDoc(fileName)) return `video/${ext}`;
    if (this.isAudioDoc(fileName)) return `audio/${ext}`;
    return ext ? `file/${ext}` : 'document';
  }

  getDocIcon(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'picture_as_pdf';
    if (['doc', 'docx'].includes(ext)) return 'description';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
    if (['ppt', 'pptx'].includes(ext)) return 'slideshow';
    return 'insert_drive_file';
  }

  getDocColorClass(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return 'doc-red';
    if (['doc', 'docx'].includes(ext)) return 'doc-blue';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'doc-green';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'doc-purple';
    return 'doc-gray';
  }
}


